import { DisplayMode } from '@2501world/lightplayer-types';
import axios from 'axios';
import * as YAML from 'yaml';
import { Logger } from '../utils/Logger';
function isExternalRef(item) {
    return item.url !== undefined;
}
/**
 * コンテンツの読み込みと互換性を吸収する
 */
export class ContentLoader {
    static async loadContent(url, config = {}) {
        const data = await this.resolveUrl(url, config);
        return this.conformContent(data);
    }
    static async conformContent(data) {
        if (data === null || data === undefined) {
            throw new Error('ERROR__CONTENT_IS_FALSY');
        }
        data.version = data.version ?? '1.0';
        switch (data.version) {
            case '1.0':
            case 'LightPlayer ContentFormat v1.1':
                return this.conformContentV1(data);
        }
        throw new Error('ERROR__UNDEFINED_VERSION');
    }
    static async conformContentV1(data) {
        const transforms = await this.resolveTransforms(data.transforms);
        const groups = data.groups.map(g => {
            g.reversed = !!g.reversed;
            if (g.reversed) {
                g.reversed = false;
                g.videoIds = g.videoIds.reverse();
            }
            delete g.reversed;
            return g;
        });
        const sources = data.sources.map(s => {
            if (s.levels) {
                s.url = s.levels.find(l => !!l)?.url ?? '';
            }
            delete s.levels;
            return s;
        });
        data.videos.forEach(video => {
            if (video['single'] || video['tiled']) {
                video.regions = [{
                        single: video['single'],
                        tiled: video['tiled'],
                        startTime: 0,
                        duration: -1
                    }];
            }
        });
        const content = {
            audios: data.audios,
            authorName: data.authorName,
            closedCaptions: data.closedCaptions ?? [],
            contentType: data.contentType ?? '',
            createdDate: data.createdDate ?? '',
            description: data.description ?? '',
            duration: data.duration ?? 'auto',
            frameRate: data.frameRate || 30,
            groups,
            markers: data.markers ?? [],
            modes: (!data.modes || data.modes === '*') ? (Object.keys(DisplayMode).map(k => DisplayMode[k])) : (Array.isArray(data.modes) ? data.modes : [data.modes]),
            offsetTime: data.offsetTime,
            playerOptions: Object.assign({
                globalScale: 1,
                frameScale: 1,
                userPlayable: true,
                userStepControl: true,
                userTransformable: true,
                backgroundColor: [0, 0, 0]
            }, data.playerOptions ?? {}),
            poster: data.poster ?? '',
            profiles: data.profiles ?? [],
            sources: sources ?? [],
            title: data.title ?? '',
            transforms,
            version: data.version,
            videos: data.videos ?? [],
            domainFilter: data.domainFilter ?? undefined
        };
        Logger.debug(content);
        return content;
    }
    /**
     * transform情報を解決する
     */
    static async resolveTransforms(src) {
        if (!src) {
            return [];
        }
        const input = [];
        if (typeof src === 'string') {
            input.push({
                name: 'Default',
                transforms: {
                    url: src
                }
            });
        }
        else {
            src.forEach(item => {
                input.push(item);
            });
        }
        return await Promise.all(input.map(async (transformSet) => {
            if (!isExternalRef(transformSet.transforms)) {
                return {
                    name: transformSet.name,
                    contentsView: this.getCalibrationContentsViewType(transformSet.contents_view),
                    transforms: transformSet.transforms
                };
            }
            const transforms = await this.resolveRefs(transformSet.transforms, async (data) => {
                let obj = [];
                const format = data.calibration_format;
                switch (format) {
                    case '2501 affine calibration ver1.0':
                    case '2501 affine calibration ver1.1':
                    case 'MasterVisions affine calibration ver1.1':
                        obj = await this.resolveCalibrationYAMLV1(data);
                        break;
                    default:
                        throw new Error('ERROR__UNDEFINED_VERSION');
                }
                return {
                    transforms: obj,
                    contentsView: this.getCalibrationContentsViewType(data.contents_view)
                };
            });
            return {
                name: transformSet.name,
                contentsView: transforms.contentsView,
                transforms: transforms.transforms
            };
        }));
    }
    static getCalibrationContentsViewType(viewType) {
        if (!['bounding_area', 'portrait', 'landscape'].some(type => type === viewType)) {
            return 'bounding_area';
        }
        return viewType;
    }
    static async resolveRefs(item, resolver) {
        if (!isExternalRef(item)) {
            return item;
        }
        let type = item.type;
        if (!type) {
            const ext = item.url.substr(item.url.lastIndexOf('.') + 1);
            switch (ext) {
                case 'json':
                    type = 'json';
                    break;
                case 'yml':
                case 'yaml':
                    type = 'yaml';
            }
        }
        const url = item.url;
        let raw = await this.resolveUrl(url, { responseType: 'text' });
        let data;
        switch (type) {
            case 'yaml':
                if (raw.startsWith('%YAML:1.0')) {
                    console.warn('YAML 1.0 is deprecated');
                    raw = raw.replace('%YAML:1.0', '%YAML 1.1');
                }
                data = YAML.parse(raw);
                break;
            case 'json':
            default:
                data = JSON.parse(raw);
        }
        return resolver ? await resolver(data) : data;
    }
    static async resolveCalibrationYAMLV1(calibration) {
        const transforms = [];
        let max = 0;
        calibration?.videos?.sort((a, b) => {
            return a.id - b.id;
        }).forEach(v => {
            max = Math.max(max, v.id);
            transforms[v.id] = {
                scale: [v.scale, v.scale],
                rotation: -v.rotation,
                offset: [v.shift[0], -v.shift[1]]
            };
        });
        for (let i = 0; i < max + 1; i++) {
            if (!transforms[i]) {
                transforms[i] = {
                    scale: [1, 1],
                    rotation: 0,
                    offset: [0, 0]
                };
            }
        }
        return transforms;
    }
    static async resolveUrl(url, config = {}) {
        try {
            const result = await axios.get(url, { responseType: 'json', ...config });
            return result.data;
        }
        catch (e) {
            console.error(e);
            return null;
        }
    }
}
//# sourceMappingURL=ContentLoader.js.map